import { DirectionsRenderer, GoogleMap, Marker } from "@react-google-maps/api";
import React, { useEffect, useState } from "react";
import { addDirectionServiceAudit } from "../../api/auditApi";

const StaticMap = ({
  activeTab = "",
  initialLocations = [],
  mapContainerStyle,
  adjustZoom,
  showDirections,
  onLocationsChanged,
  onDirectionsChanged,
  adjustZoomForMultipleLocations,
  calculateMapCenter,
  jobId,
  requestId,
}) => {
  const [mapCenter, setMapCenter] = useState(initialLocations);

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(10);

  useEffect(() => {
    if (initialLocations.length === 2) {
      const zoomLevel = adjustZoom(initialLocations[0], initialLocations[1]);
      setZoom(zoomLevel);
      setMapCenter({
        lat: (initialLocations[0].lat + initialLocations[1].lat) / 2,
        lng: (initialLocations[0].lng + initialLocations[1].lng) / 2,
      });
    } else if (initialLocations.length === 1) {
      setMapCenter(initialLocations[0]);
      setZoom(15); // Default zoom level for a single location
    } else if (initialLocations.length > 2) {
      const zoomLevel = adjustZoomForMultipleLocations(initialLocations);
      setZoom(zoomLevel);
      setMapCenter(calculateMapCenter(initialLocations));
    }
  }, [initialLocations, adjustZoom, adjustZoomForMultipleLocations]);

  useEffect(() => {
    if (map && initialLocations.length >= 2 && showDirections) {
      const directionsService = new window.google.maps.DirectionsService();

      const firstLocation = initialLocations[0];
      const lastLocation = initialLocations[initialLocations.length - 1];

      if (!firstLocation || !lastLocation) {
        return;
      }

      if (
        (firstLocation?.lng === 0 && firstLocation.lat === 0) ||
        (lastLocation?.lng === 0 && lastLocation.lat === 0)
      ) {
        return;
      }

      directionsService.route(
        {
          origin: firstLocation, // Starting point
          destination: lastLocation, // Endpoint
          waypoints: initialLocations.slice(1, -1).map((location) => ({
            location,
            stopover: true, // Ensure stopovers to recalculate
          })),
          travelMode: window.google.maps.TravelMode.DRIVING,
          avoidHighways: true,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionsResponse(result);
            // Trigger the parent component to update the locations order
            if (onLocationsChanged) {
              const updatedLocations = result.routes[0].legs.map(
                (leg, index) => ({
                  lat: leg.start_location.lat(),
                  lng: leg.start_location.lng(),
                  arrivalTime: leg.arrival_time?.text,
                  departureTime: leg.departure_time?.text,
                }),
              );
              onLocationsChanged(updatedLocations);
            }
            if (onDirectionsChanged) {
              onDirectionsChanged(result);
            }
          } else {
            console.error(`Failed to load directions: ${status}`);
            setDirectionsResponse(null); // Ensuring directions state is reset on failure
          }
        },
      );
      addDirectionServiceAudit({
        user: "FE STATIC MAP",
        jobId: jobId,
        reqId: requestId,
        from: {
          locId: null,
          lon: firstLocation?.lng,
          lat: firstLocation?.lat,
        },
        to: {
          locId: null,
          lon: lastLocation?.lng,
          lat: lastLocation?.lng,
        },
      });
    } else {
      setDirectionsResponse(null);
    }
  }, [
    map,
    initialLocations,
    showDirections,
    onLocationsChanged,
    onDirectionsChanged,
  ]);

  const renderCustomMarkers = () => {
    console.log("The Initial Locations: ", initialLocations);
    return initialLocations?.map((location, index) => {
      let label = "";
      if (index === 0) {
        console.log("Start: ", index);
        label = "S"; // Start point
      } else if (index === initialLocations.length - 1) {
        console.log(index);
        label = "E"; // End point
      } else {
        console.log("Other: ", index);
        label = (index + 1).toString(); // Waypoints, using index + 1 for clarity
      }
      return <Marker key={index} position={location} label={label} />;
    });
  };

  const renderRouteSummary = () => {
    if (!directionsResponse) return null;

    const route = directionsResponse.routes[0];
    let totalDistance = 0;
    let totalDuration = 0; // Duration in seconds

    route.legs.forEach((leg) => {
      totalDistance += leg.distance.value;
      totalDuration += leg.duration.value;
    });

    // Convert duration to hours and minutes
    const durationHours = Math.floor(totalDuration / 3600);
    const durationMinutes = Math.ceil((totalDuration % 3600) / 60);
    let formattedDuration = "";

    if (durationHours > 0) {
      formattedDuration += `${durationHours} h${durationHours > 1 ? "s" : ""} `;
    }
    if (durationMinutes > 0) {
      formattedDuration += `${durationMinutes} min${durationMinutes > 1 ? "s" : ""}`;
    }

    return (
      <div
        style={{
          display: "flex",
          backgroundColor: "rgba(255, 255, 255, 1)",
          position: "absolute",
          zIndex: "101",
          bottom: "0",
          left: "0",
          padding: "5px",
        }}
      >
        <p>
          <b>
            {formattedDuration} ({(totalDistance / 1000).toFixed(2)} km)
          </b>
        </p>
      </div>
    );
  };

  return (
    <div>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={zoom}
        onLoad={setMap}
      >
        {showDirections && directionsResponse && (
          <DirectionsRenderer
            directions={directionsResponse}
            options={{ suppressMarkers: true }} // Suppress default markers
          />
        )}
        {renderCustomMarkers()}
      </GoogleMap>
      {(activeTab === "job" ||
        activeTab === "status" ||
        activeTab === "request") &&
        renderRouteSummary()}
    </div>
  );
};

export default StaticMap;
